import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield } from 'lucide-react';

/**
 * Auftragsverarbeitungsvertrag (AVV) / Data Processing Agreement (DPA)
 *
 * Public legal page linked from the click-wrap modal in InterviewHub.
 * Version is pinned via `?v=` query (default = latest) and the click-wrap
 * flow records the same string server-side via interview-service, so each
 * acceptance is bound to the exact text the company saw.
 *
 * URL: /legal/avv?v=1.0&lang=de|en
 */
const Avv: React.FC = () => {
  const { i18n } = useTranslation();
  const [params] = useSearchParams();
  const langParam = params.get('lang');
  const lang: 'de' | 'en' = (langParam === 'en' || langParam === 'de')
    ? langParam
    : (i18n.language.startsWith('de') ? 'de' : 'en');
  const version = params.get('v') || '1.2';

  const t = lang === 'de' ? COPY_DE : COPY_EN;

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 min-h-[calc(100vh-200px)]">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">{t.title}</h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <span className="inline-block bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded whitespace-nowrap">
            {t.version}: {version}
          </span>
        </div>
      </div>

      <p className="text-slate-600 mb-8 leading-relaxed">{t.intro}</p>

      {t.sections.map((section) => (
        <section key={section.heading} className="mb-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-3">{section.heading}</h2>
          {section.paragraphs.map((para, i) => (
            <p key={i} className="text-slate-600 leading-relaxed mb-3">{para}</p>
          ))}
          {section.list && (
            <ul className="list-disc pl-6 text-slate-600 space-y-1.5 mt-2">
              {section.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
          {section.footnote && (
            <p className="text-slate-500 text-sm leading-relaxed mt-3 italic">{section.footnote}</p>
          )}
        </section>
      ))}

      <hr className="my-10 border-slate-200" />

      <div className="text-sm text-slate-500">
        <p>{t.contact}</p>
      </div>
    </div>
  );
};

interface Section {
  heading: string;
  paragraphs: string[];
  list?: string[];
  footnote?: string;
}

const COPY_DE: {
  title: string;
  version: string;
  intro: string;
  contact: string;
  sections: Section[];
} = {
  title: 'Auftragsverarbeitungsvertrag (AVV)',
  version: 'Version',
  intro: 'Dieser Auftragsverarbeitungsvertrag (AVV) regelt die Verarbeitung personenbezogener Daten durch Cevee als Auftragsverarbeiter im Rahmen der KI-Interview-Funktion gemäß Art. 28 DSGVO. Mit der Annahme im Cevee-Dashboard durch eine vertretungsberechtigte Person Ihres Unternehmens kommt dieser AVV wirksam zustande.',
  contact: 'Bei Fragen zu diesem AVV kontaktieren Sie bitte unser Datenschutzteam: legal@cevee.eu',
  sections: [
    {
      heading: '1. Parteien',
      paragraphs: [
        'Verantwortlicher: Das Unternehmen, in dessen Namen der AVV im Cevee-Dashboard akzeptiert wurde.',
        'Auftragsverarbeiter: Cevee (Anbieter der Plattform, vollständige Anbieterdaten siehe Impressum).',
      ],
    },
    {
      heading: '2. Gegenstand und Dauer der Verarbeitung',
      paragraphs: [
        'Cevee verarbeitet personenbezogene Daten von Bewerber:innen, die Sie zu einem KI-Interview einladen oder die über Cevee an einem KI-Interview teilnehmen. Die Verarbeitung dauert solange an, wie Sie die KI-Interview-Funktion nutzen und Daten in Ihrem Cevee-Account gespeichert sind.',
      ],
    },
    {
      heading: '3. Weisungsgebundene Verarbeitung',
      paragraphs: [
        'Cevee verarbeitet die personenbezogenen Daten ausschließlich auf dokumentierte Weisung des Verantwortlichen, einschließlich hinsichtlich der Übermittlung in Drittländer, sofern Cevee nicht durch das Unionsrecht oder das Recht eines Mitgliedstaats, dem Cevee unterliegt, hierzu verpflichtet ist (Art. 28 Abs. 3 lit. a DSGVO). Die Konfiguration der Templates, Bewertungskriterien, Aufbewahrungsfristen und ATS-Weiterleitung im Cevee-Dashboard gilt als dokumentierte Weisung.',
        'Ist Cevee der Auffassung, dass eine Weisung gegen die DSGVO oder andere Datenschutzbestimmungen verstößt, informiert Cevee den Verantwortlichen unverzüglich. Cevee ist berechtigt, die Ausführung der betreffenden Weisung bis zu deren Bestätigung oder Änderung auszusetzen.',
      ],
    },
    {
      heading: '4. Art und Zweck der Verarbeitung',
      paragraphs: [
        'Zweck der Verarbeitung ist die Durchführung KI-gestützter Vorstellungsgespräche sowie die KI-gestützte Bewerbungs-Vorauswahl (Lite-ATS) im Auftrag des Verantwortlichen, einschließlich:',
      ],
      list: [
        'Entgegennahme von Direktbewerbungen über einen vom Verantwortlichen geteilten Karriere-Link (Lite-ATS), auch von Personen ohne Cevee-Konto',
        'KI-gestützte Vorauswahl: Abgleich des eingereichten Lebenslaufs mit der Stellenbeschreibung, Erstellung einer Empfehlung/Scorecard und Verwaltung des mehrstufigen Auswahl-Funnels (inkl. optionaler automatischer Einladung zur nächsten Stufe bei positiver Einschätzung; Absagen ausschließlich nach menschlicher Prüfung)',
        'Aufzeichnung von Video-, Audio- und (sofern für das Template aktiviert) Bildschirm-Inhalten während des Interviews (browserbasierte Aufnahme, Speicherung in der EU)',
        'Live-Sprachverarbeitung durch die KI-Interviewerin (Speech-to-Speech über den Azure OpenAI Service)',
        'Erstellung von Transkripten in der Originalsprache (über den Azure OpenAI Service, EU)',
        'Erfassung und Speicherung von Live-Coding-Eingaben und Whiteboard-Skizzen (System-Design) während des Interviews',
        'KI-gestützte Bewertung anhand der vom Verantwortlichen definierten Kriterien (Scorecard, Azure OpenAI Service)',
        'Bereitstellung der Bewertung zur abschließenden Prüfung durch Recruiter:innen (Human-in-the-Loop gemäß Art. 22 DSGVO)',
        'Speicherung im Rahmen der vereinbarten Aufbewahrungsfristen mit automatisierter Löschung',
      ],
    },
    {
      heading: '5. Art der personenbezogenen Daten',
      paragraphs: ['Folgende Datenkategorien werden verarbeitet:'],
      list: [
        'Stammdaten der Bewerber:innen (Name, E-Mail, ggf. Profilangaben)',
        'Bewerbungsunterlagen aus Direktbewerbungen (Lebenslauf, ggf. Freitext/Anschreiben)',
        'KI-Vorauswahl-Ergebnisse (Eignungs-Score, Empfehlung, Begründung) und Funnel-Status',
        'Video-, Audio- und ggf. Bildschirmaufzeichnung des Interviews',
        'Transkript des gesprochenen Inhalts (zeitgestempelte Turns)',
        'Live-Coding-Snapshots (Quellcode, Sprache, Submission-Events)',
        'Whiteboard-/System-Design-Snapshots (Komponenten + Verbindungen, JSON)',
        'KI-generierte Bewertungen, Begründungen und Empfehlungen',
        'Audit-Metadaten (Zeitstempel, Gerät, IP-Adresse, WebSocket-Session-IDs)',
      ],
    },
    {
      heading: '6. Kategorien betroffener Personen',
      paragraphs: [
        'Bewerber:innen, die vom Verantwortlichen zu einem KI-Interview eingeladen werden oder über Cevee selbstständig an Interviews teilnehmen, sowie Personen, die sich über einen Karriere-Link direkt auf eine Stelle des Verantwortlichen bewerben (auch ohne Cevee-Konto).',
      ],
    },
    {
      heading: '7. Eingesetzte Unterauftragsverarbeiter',
      paragraphs: [
        'Der Verantwortliche erteilt Cevee die allgemeine Genehmigung zum Einsatz der nachfolgend genannten Unterauftragsverarbeiter. Alle Subunternehmer sind vertraglich an DSGVO-konforme Standards (Art. 28 Abs. 4 DSGVO) gebunden und bestätigen die ausschließliche Verarbeitung in EU-Regionen ohne Trainingsnutzung:',
      ],
      list: [
        'Microsoft Azure (EU, France Central) - Hosting, Datenbank, Speicher, Container-Infrastruktur, Key Vault',
        'Microsoft Azure OpenAI Service (EU, France Central / Sweden Central) - Sprach- und Bewertungs-KI für Live-Voice, Transkription und Scorecard. Keine Trainingsnutzung. EU Data Boundary aktiviert.',
        'Supabase (EU, Frankfurt) - ausschließlich Authentifizierung (Keine Interviewinhalte)',
        'Pinecone (EU, Frankfurt) - semantischer Suchindex (nicht für Interviewinhalte verwendet)',
      ],
      footnote:
        'ATS-Anbieter (z.B. Ashby, Personio, Needle, Workday), an die Cevee auf Weisung des Verantwortlichen Bewerber:innendaten übermittelt, sind keine Unterauftragsverarbeiter von Cevee im Sinne von Art. 28 Abs. 4 DSGVO. Der Verantwortliche unterhält den Vertrag direkt mit dem jeweiligen ATS-Anbieter; Cevee fungiert als technische Schnittstelle und führt die Übermittlung ausschließlich nach dokumentierter Weisung des Verantwortlichen aus. Eine Liste der von Cevee unterstützten ATS-Anbieter wird in der Plattform (Company Settings → ATS Integration) bereitgestellt.',
    },
    {
      heading: '8. Änderung von Unterauftragsverarbeitern',
      paragraphs: [
        'Beabsichtigt Cevee, einen weiteren Unterauftragsverarbeiter hinzuzuziehen oder einen bestehenden zu ersetzen, informiert Cevee den Verantwortlichen mindestens 30 Tage vor Wirksamwerden der Änderung in Textform (z.B. per E-Mail an die hinterlegte Admin-Adresse oder per Hinweis im Dashboard) (Art. 28 Abs. 2 DSGVO).',
        'Der Verantwortliche kann der Änderung innerhalb von 30 Tagen aus wichtigem datenschutzrechtlichen Grund widersprechen. Lässt sich der Widerspruch nicht durch eine zumutbare Anpassung ausräumen, ist der Verantwortliche berechtigt, die betroffene Funktion oder den Vertrag mit Wirkung zum geplanten Änderungsdatum zu kündigen.',
      ],
    },
    {
      heading: '9. Verarbeitungsort',
      paragraphs: [
        'Die gesamte von Cevee selbst durchgeführte Verarbeitung - einschließlich Speicherung, KI-Modell-Inferenz, Transkription und Scorecard-Erstellung - erfolgt ausschließlich innerhalb der EU (Azure-Region France Central; bei Modellverfügbarkeit zusätzlich Sweden Central, ebenfalls EU). Eine Übermittlung von Interview-Inhalten in Drittländer außerhalb der EU/EWR durch Cevee findet nicht statt. Ausnahme: Wenn der Verantwortliche die ATS-Weiterleitungsfunktion aktiviert und dabei einen ATS-Anbieter mit Sitz oder Hosting außerhalb der EU wählt (z.B. Ashby Inc., USA), erfolgt die Übermittlung der Bewerber:innendaten an dieses ATS auf Weisung des Verantwortlichen. Die Pflicht zur Bereitstellung geeigneter Garantien für diese Drittland-Übermittlung (z.B. EU-Standardvertragsklauseln, Transfer Impact Assessment) liegt beim Verantwortlichen als Vertragspartei des ATS-Anbieters.',
      ],
    },
    {
      heading: '10. Technische und organisatorische Maßnahmen (TOM)',
      paragraphs: ['Cevee implementiert insbesondere folgende Maßnahmen:'],
      list: [
        'Verschlüsselung im Transit (TLS 1.3, WSS für Live-Audio) und at-rest (AES-256)',
        'Rollen- und Berechtigungskonzept (RBAC) mit detaillierten Audit-Logs',
        'Managed Identities und Key-Vault-basiertes Secret-Management (keine Klartext-Credentials im Code)',
        'Pseudonymisierung wo möglich (Audit-Logs, ATS-Weiterleitung)',
        'KI-Safety-Layer: System-Prompt-Hardening, Prompt-Injection-Erkennung, Themenfilter, 4-stufige Rate-Limits',
        'Regelmäßige Pen-Tests und Schwachstellen-Scans',
        'Automatisierte Datenaufbewahrungs-Pipeline mit zeitgesteuerten Hintergrund-Jobs für DSGVO-konforme Löschung',
        'Detaillierter Audit-Log für jede DSGVO-relevante Aktion (Akzeptanz, Widerruf, Datenexport, Löschung)',
      ],
    },
    {
      heading: '11. Unterstützung des Verantwortlichen',
      paragraphs: [
        'Cevee unterstützt den Verantwortlichen unter Berücksichtigung der Art der Verarbeitung und der ihm zur Verfügung stehenden Informationen mit geeigneten technischen und organisatorischen Maßnahmen bei der Erfüllung seiner Pflichten (Art. 28 Abs. 3 lit. e und f DSGVO), insbesondere:',
      ],
      list: [
        'bei der Beantwortung von Anträgen betroffener Personen auf Wahrnehmung ihrer Rechte (Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit, Widerspruch) - über Selfservice-Funktionen im Dashboard sowie auf Anfrage',
        'bei der Gewährleistung der Sicherheit der Verarbeitung (Art. 32 DSGVO)',
        'bei der Meldung und Dokumentation von Datenschutzverletzungen (Art. 33, 34 DSGVO)',
        'bei der Durchführung von Datenschutz-Folgenabschätzungen (Art. 35 DSGVO) und etwaigen vorherigen Konsultationen (Art. 36 DSGVO) durch Bereitstellung der erforderlichen Informationen zur Verarbeitung',
      ],
    },
    {
      heading: '12. Rechte des Verantwortlichen',
      paragraphs: ['Der Verantwortliche kann jederzeit:'],
      list: [
        'Aufzeichnungen und Daten einzelner Interviews löschen lassen',
        'Den AVV widerrufen (sofortige Deaktivierung der KI-Interview-Funktion)',
        'Daten in maschinenlesbarem Format exportieren',
        'Audit-Logs einsehen',
        'Auditrechte gemäß Art. 28 Abs. 3 lit. h DSGVO ausüben (Cevee stellt die zum Nachweis der Einhaltung erforderlichen Informationen bereit und ermöglicht Überprüfungen, einschließlich Inspektionen, durch den Verantwortlichen oder eine:n von ihm beauftragte:n Prüfer:in)',
      ],
    },
    {
      heading: '13. Automatisierte Entscheidungsfindung (Art. 22 DSGVO)',
      paragraphs: [
        'Die KI-gestützte Scorecard stellt KEINE ausschließlich automatisierte Entscheidung im Sinne von Art. 22 DSGVO dar. Alle KI-Bewertungen werden den Recruiter:innen des Verantwortlichen als Empfehlung vorgelegt; die finale Einstellungs- oder Auswahlentscheidung trifft ausschließlich ein Mensch (Human-in-the-Loop). Der Verantwortliche ist verpflichtet, dieses Prinzip in seinen internen Recruitingprozessen einzuhalten und Kandidat:innen entsprechend zu informieren.',
      ],
    },
    {
      heading: '14. Pflichten der Mitarbeiter:innen',
      paragraphs: [
        'Alle Cevee-Mitarbeiter:innen mit Zugang zu personenbezogenen Daten sind auf das Datengeheimnis verpflichtet und entsprechend geschult.',
      ],
    },
    {
      heading: '15. Benachrichtigung bei Datenschutzvorfällen',
      paragraphs: [
        'Cevee informiert den Verantwortlichen unverzüglich, spätestens jedoch innerhalb von 48 Stunden nach Bekanntwerden, über Datenschutzvorfälle gemäß Art. 33 DSGVO. Damit verbleibt dem Verantwortlichen ausreichend Zeit, seine eigene 72-Stunden-Meldefrist gegenüber der zuständigen Aufsichtsbehörde einzuhalten.',
      ],
    },
    {
      heading: '16. Aufbewahrung, Laufzeit und Beendigung',
      paragraphs: [
        'Aufzeichnungen, Transkripte und KI-Bewertungen werden standardmäßig 6 Monate nach Abschluss bzw. Absage des Verfahrens automatisiert gelöscht, sofern der Verantwortliche im Dashboard keine kürzere Frist konfiguriert. Der Verantwortliche ist dafür verantwortlich, dass die gewählte Aufbewahrungsdauer mit den für ihn geltenden Vorgaben (z.B. AGG-Klagefristen) vereinbar ist.',
        'Dieser AVV gilt für die Dauer der Nutzung der KI-Interview-Funktion. Nach Beendigung werden nach Wahl des Verantwortlichen sämtliche personenbezogene Daten innerhalb von 30 Tagen gelöscht oder zuvor exportiert; bestehende Kopien werden gelöscht, soweit keine gesetzliche Aufbewahrungspflicht entgegensteht.',
      ],
    },
  ],
};

const COPY_EN: typeof COPY_DE = {
  title: 'Data Processing Agreement (DPA)',
  version: 'Version',
  intro: 'This Data Processing Agreement (DPA) governs the processing of personal data by Cevee as data processor for the AI Interview feature under GDPR Art. 28. Acceptance in the Cevee dashboard by an authorized representative of your company constitutes a binding agreement.',
  contact: 'For questions regarding this DPA please contact our data protection team: legal@cevee.eu',
  sections: [
    {
      heading: '1. Parties',
      paragraphs: [
        'Controller: The company on whose behalf the DPA was accepted in the Cevee dashboard.',
        'Processor: Cevee (platform provider, full details see Imprint).',
      ],
    },
    {
      heading: '2. Subject Matter and Duration',
      paragraphs: [
        'Cevee processes personal data of candidates whom you invite to or who participate in AI interviews via Cevee. Processing continues as long as you use the AI Interview feature and data is stored in your Cevee account.',
      ],
    },
    {
      heading: '3. Processing on Documented Instructions',
      paragraphs: [
        'Cevee processes the personal data only on documented instructions from the controller, including with regard to transfers to third countries, unless required to do so by Union or Member State law to which Cevee is subject (GDPR Art. 28(3)(a)). The configuration of templates, evaluation criteria, retention periods and ATS forwarding in the Cevee dashboard constitutes documented instructions.',
        'If Cevee considers that an instruction infringes the GDPR or other data protection provisions, Cevee informs the controller without undue delay. Cevee is entitled to suspend the execution of the instruction concerned until it is confirmed or amended.',
      ],
    },
    {
      heading: '4. Nature and Purpose',
      paragraphs: ['Purpose is the execution of AI-led interviews and AI-assisted application pre-selection (Lite-ATS) on behalf of the controller, including:'],
      list: [
        'Receiving direct applications via a career link shared by the controller (Lite-ATS), including from people without a Cevee account',
        'AI-assisted pre-selection: matching the submitted CV against the job description, producing a recommendation/scorecard, and managing the multi-stage selection funnel (incl. optional automatic invitation to the next stage on a positive assessment; rejections only after human review)',
        'Recording of video and audio during the interview (browser-based recording, EU storage)',
        'Live speech-to-speech processing by the AI interviewer (via Azure OpenAI Service)',
        'Generation of transcripts in the original language (via Azure OpenAI Service, EU)',
        'Capture and storage of live coding inputs and whiteboard sketches (system design) during the interview',
        'AI-supported evaluation against controller-defined criteria (scorecard, Azure OpenAI Service)',
        'Presentation of evaluations to recruiters for final review (human-in-the-loop under GDPR Art. 22)',
        'Storage during the agreed retention period with automated deletion',
      ],
    },
    {
      heading: '5. Type of Personal Data',
      paragraphs: ['The following data categories are processed:'],
      list: [
        'Candidate master data (name, email, optional profile data)',
        'Application documents from direct applications (CV, optional free text / cover note)',
        'AI pre-selection results (fit score, recommendation, rationale) and funnel status',
        'Video and audio recording of the interview',
        'Transcript of spoken content (time-stamped turns)',
        'Live coding snapshots (source code, language, submission events)',
        'Whiteboard / system design snapshots (components + connections, JSON)',
        'AI-generated evaluations, justifications and recommendations',
        'Audit metadata (timestamps, device, IP address, WebSocket session IDs)',
      ],
    },
    {
      heading: '6. Categories of Data Subjects',
      paragraphs: ['Candidates invited by the controller or self-onboarding into Cevee interviews, as well as people who apply directly to a role of the controller via a career link (including without a Cevee account).'],
    },
    {
      heading: '7. Subprocessors',
      paragraphs: [
        'The controller grants Cevee general authorization to engage the subprocessors listed below. All subprocessors are contractually bound to GDPR-compliant standards (GDPR Art. 28(4)) and confirm EU-only processing without training reuse:',
      ],
      list: [
        'Microsoft Azure (EU, France Central) - hosting, database, storage, container infrastructure, Key Vault',
        'Microsoft Azure OpenAI Service (EU, France Central / Sweden Central) - speech and evaluation AI for live voice, transcription and scorecard. No training reuse. EU Data Boundary enabled.',
        'Supabase (EU, Frankfurt) - authentication only (no interview content)',
        'Pinecone (EU, Frankfurt) - semantic search index (not used for interview content)',
      ],
      footnote:
        'ATS providers (e.g. Ashby, Personio, Needle, Workday) to which Cevee transfers candidate data on the controller\u2019s instructions are NOT subprocessors of Cevee within the meaning of Art. 28(4) GDPR. The controller maintains a direct contract with the respective ATS provider; Cevee acts solely as a technical interface and performs the transfer exclusively on the controller\u2019s documented instructions. A list of ATS providers supported by Cevee is made available in the platform (Company Settings \u2192 ATS Integration).',
    },
    {
      heading: '8. Changes to Subprocessors',
      paragraphs: [
        'Where Cevee intends to engage an additional subprocessor or replace an existing one, Cevee informs the controller in text form (e.g. by email to the registered admin address or via a dashboard notice) at least 30 days before the change takes effect (GDPR Art. 28(2)).',
        'The controller may object to the change within 30 days on legitimate data-protection grounds. If the objection cannot be resolved by a reasonable adjustment, the controller is entitled to terminate the affected feature or the contract with effect from the planned change date.',
      ],
    },
    {
      heading: '9. Processing Location',
      paragraphs: [
        'All processing performed by Cevee itself - including storage, AI model inference, transcription and scorecard generation - happens exclusively within the EU (Azure region France Central; where models are unavailable, Sweden Central, also EU). Cevee does not transfer interview content outside the EU/EEA. Exception: When the controller activates the ATS forwarding feature and selects an ATS provider headquartered or hosted outside the EU (e.g. Ashby Inc., USA), the candidate data is transferred to that ATS on the controller\u2019s instructions. The obligation to provide appropriate safeguards for that third-country transfer (e.g. EU Standard Contractual Clauses, Transfer Impact Assessment) lies with the controller as the contractual party of the ATS provider.',
      ],
    },
    {
      heading: '10. Technical and Organizational Measures',
      paragraphs: ['Cevee implements in particular the following measures:'],
      list: [
        'Encryption in transit (TLS 1.3, WSS for live audio) and at rest (AES-256)',
        'Role-based access control (RBAC) with detailed audit logs',
        'Managed identities and Key Vault based secret management (no plaintext credentials in code)',
        'Pseudonymization where applicable (audit logs, ATS forwarding)',
        'AI safety layer: system prompt hardening, prompt-injection detection, topic filtering, 4-layer rate limiting',
        'Regular pen-tests and vulnerability scans',
        'Automated data retention pipeline with scheduled background jobs for GDPR-compliant deletion',
        'Detailed audit log for every GDPR-relevant action (acceptance, withdrawal, export, deletion)',
      ],
    },
    {
      heading: '11. Assistance to the Controller',
      paragraphs: [
        'Taking into account the nature of the processing and the information available to it, Cevee assists the controller by appropriate technical and organizational measures in fulfilling its obligations (GDPR Art. 28(3)(e) and (f)), in particular:',
      ],
      list: [
        'in responding to requests from data subjects exercising their rights (access, rectification, erasure, restriction, portability, objection) - via self-service functions in the dashboard and on request',
        'in ensuring the security of processing (GDPR Art. 32)',
        'in notifying and documenting personal data breaches (GDPR Art. 33, 34)',
        'in carrying out data protection impact assessments (GDPR Art. 35) and any prior consultations (GDPR Art. 36) by providing the necessary information about the processing',
      ],
    },
    {
      heading: '12. Controller Rights',
      paragraphs: ['The controller may at any time:'],
      list: [
        'Delete recordings and data of individual interviews',
        'Withdraw the DPA (immediate deactivation of the AI Interview feature)',
        'Export data in machine-readable format',
        'Inspect audit logs',
        'Exercise audit rights under GDPR Art. 28(3)(h) (Cevee makes available the information necessary to demonstrate compliance and allows for and contributes to audits, including inspections, conducted by the controller or an auditor mandated by the controller)',
      ],
    },
    {
      heading: '13. Automated Decision-Making (GDPR Art. 22)',
      paragraphs: [
        'The AI-supported scorecard does NOT constitute a solely automated decision within the meaning of GDPR Art. 22. All AI evaluations are presented to the controller\'s recruiters as recommendations; the final hiring or selection decision is made exclusively by a human (human-in-the-loop). The controller is required to enforce this principle in their internal recruiting processes and to inform candidates accordingly.',
      ],
    },
    {
      heading: '14. Staff Obligations',
      paragraphs: [
        'All Cevee employees with access to personal data are bound to confidentiality and trained accordingly.',
      ],
    },
    {
      heading: '15. Notification of Data Breaches',
      paragraphs: [
        'Cevee notifies the controller without undue delay, no later than 48 hours after becoming aware of a personal data breach within the meaning of GDPR Art. 33. This leaves the controller sufficient time to meet its own 72-hour notification deadline to the competent supervisory authority.',
      ],
    },
    {
      heading: '16. Retention, Term and Termination',
      paragraphs: [
        'Recordings, transcripts and AI evaluations are deleted automatically 6 months after the process concludes or is rejected by default, unless the controller configures a shorter period in the dashboard. The controller is responsible for ensuring that the chosen retention period is compatible with the requirements applicable to it (e.g. limitation periods for discrimination claims).',
        'This DPA applies for the duration of usage of the AI Interview feature. Upon termination, at the controller\u2019s choice all personal data is deleted within 30 days or exported beforehand; existing copies are deleted unless a statutory retention obligation applies.',
      ],
    },
  ],
};

export default Avv;
